import * as React from 'react'
import { cn } from '@/lib/utils'

/**
 * PageShell - Standardized page container
 *
 * Provides consistent max-width and the panel content inset for all pages.
 * Use this as the root wrapper for page content.
 *
 * Inset (White signal v2, Paper "App v3 · … · LP-aligned"): 28px top, 36px
 * sides, 36px bottom inside the white panel. When the layout already applies
 * that inset (DashboardLayout wraps its outlet in <ShellContentProvider>),
 * PageShell drops its own padding so the two never stack.
 *
 * @example
 * ```tsx
 * <PageShell>
 *   <PageHeader title="Dashboard" />
 *   <div>Content here</div>
 * </PageShell>
 * ```
 */
interface PageShellProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Maximum width of the content area (default: 1600px) */
  maxWidth?: string
  /** Padding override. Defaults to the panel inset, or none inside a padded layout. */
  padding?: string
}

const PANEL_INSET = 'px-4 pt-5 pb-8 sm:px-9 sm:pt-7 sm:pb-9'

const ShellContentContext = React.createContext(false)

/** Marks a subtree whose layout already applies the panel inset. */
export function ShellContentProvider({ children }: { children: React.ReactNode }) {
  return <ShellContentContext.Provider value={true}>{children}</ShellContentContext.Provider>
}

export const PageShell = React.forwardRef<HTMLDivElement, PageShellProps>(
  ({ className, maxWidth = 'max-w-[1600px]', padding, ...props }, ref) => {
    const insetByLayout = React.useContext(ShellContentContext)
    return (
      <div
        ref={ref}
        className={cn(
          'w-full mx-auto',
          maxWidth,
          padding ?? (insetByLayout ? '' : PANEL_INSET),
          className
        )}
        {...props}
      />
    )
  }
)
PageShell.displayName = 'PageShell'
